<template>
	<div class="small-container add-profile">
		<IconAppLogo />
		<div class="description">
			<h1 class="subtitle">{{ editProfileMode ? $t("profile.edit") : $t("profile.create") }}</h1>
		</div>

		<form @submit.prevent="handleSubmit" v-focus-section>
			<div>
				<label for="profileName">{{ $t("profile.name") }}</label>
				<input id="profileName" v-focus v-model="profileName" type="text" required />
			</div>

			<h2 class="vl-font-size--18 color-white">
				{{ editProfileMode ? $t("profile.change_icon") : $t("profile.choose_icon") }}
			</h2>

			<Splide
				v-focus-section
				ref="splideRef"
				:options="{ type: 'slide', perPage: 1, focus: 'center', arrows: true, pagination: false }"
				aria-label="Profile Images"
			>
				<SplideSlide v-focus v-for="(item, key) in available_images" :key="key" :data-id="item.id">
					<img :src="url + item?.field_media_image?.uri?.url" alt="" />
				</SplideSlide>
			</Splide>

			<button class="submit" v-focus :disabled="!isFormValid">
				{{ loading ? $t("profile.saving") : $t("profile.save") }}
			</button>

			<p v-if="error" class="color-red profile-message">{{ error }}</p>
			<p v-if="success" class="color-white profile-message">{{ $t("profile.success") }}!</p>
		</form>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted, nextTick } from "vue"
	import { useUserProfiles } from "../composables/useUserProfile"
	import api from "../composables/useApi"
	import { url } from "../helper"
	import { useRouter } from "vue-router"
	import IconAppLogo from "../icons/IconAppLogo.vue"
	import { useUserStore } from "../stores/user"

	const userStore = useUserStore()
	const router = useRouter()
	const edit_profile = computed(() => userStore.state.edit_profile)
	const editProfileMode = computed(() => !!edit_profile.value?.id)

	const profileName = ref(edit_profile.value?.field_name || "")
	const success = ref(false)
	const selectedImageId = ref<string | null>(edit_profile.value?.field_profile_photo?.data?.id || null)
	const splideRef = ref<any>(null)
	const available_images = ref([])

	const { addProfileToUser, loading, error } = useUserProfiles()

	const isFormValid = computed(() => profileName.value.trim() !== "")

	const getImages = async () => {
		try {
			const res = await api.get("/jsonapi/media/image?include=field_media_image&jsonapi_include=1&filter[status]=1")
			const images = res.data?.data || []
			available_images.value = images
			if (!selectedImageId.value && images.length > 0) selectedImageId.value = images[0].id
		} catch (err) {
			console.error("Error fetching images:", err)
			available_images.value = []
			selectedImageId.value = null
		}
	}
	getImages()

	const getJwtPayloadSafe = (): any | null => {
		const token = localStorage.getItem("jwt")
		if (!token) return null
		try {
			const base64 = token.split(".")[1]
			if (!base64) return null
			return JSON.parse(atob(base64))
		} catch {
			return null
		}
	}

	const getActiveSlideDataId = () => {
		const splide = splideRef.value?.splide
		if (!splide) return null
		const index = splide.index
		const activeSlideEl = splide.Components.Slides.getAt(index)?.slide
		selectedImageId.value = activeSlideEl?.getAttribute("data-id") || null
		return selectedImageId.value
	}

	const handleSubmit = async () => {
		success.value = false
		const payload = getJwtPayloadSafe()

		try {
			if (editProfileMode.value) {
				// PATCH existing profile
				await api.patch(
					`/jsonapi/profile/subscriber_profile/${edit_profile.value.id}`,
					{
						data: {
							type: "profile--subscriber_profile",
							id: edit_profile.value.id,
							attributes: { field_name: profileName.value },
							relationships: { field_profile_photo: { data: { type: "media--image", id: selectedImageId.value } } },
						},
					},
					{ headers: { "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json" } }
				)
			} else {
				// POST new profile
				await addProfileToUser({
					attributes: { field_name: profileName.value, label: profileName.value },
					relationships: {
						field_profile_photo: { data: { type: "media--image", id: selectedImageId.value } },
						uid: { data: { type: "user--user", id: payload?.uuid } },
					},
				})
			}

			success.value = true
			if (editProfileMode.value) {
				window.location.href = "/home"
			} else {
				router.push("/home")
			}
		} catch (err) {
			console.error("Error saving profile:", err)
		}
	}

	onMounted(async () => {
		if (editProfileMode.value && !edit_profile.value?.id) {
			router.replace({ name: "Profile" })
		}
		await nextTick()
		if (splideRef.value?.splide) {
			getActiveSlideDataId()
			splideRef.value.splide.on("move", getActiveSlideDataId)
		}
	})
</script>

<style lang="scss" scoped>
	@import url("../scss/form.scss");
	.add-profile {
		padding-top: 100px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 30px;

		img {
			border-radius: 50%;
			border: 2px solid white;
			margin-bottom: 20px;
			width: 100px;
			height: 100px;
		}
	}
	input {
		margin-bottom: 30px;
	}
	label {
		display: block;
	}
	.profile-message {
		margin: 0 auto;
		text-align: center;
	}
	h2 {
		text-align: center;
		margin-bottom: 10px;
	}
	form {
		width: 100%;
	}
	.splide__slide {
		display: flex;
		justify-content: center;
		align-items: center;
	}
	button {
		margin-top: 24px !important;
	}
</style>

<style lang="scss">
	.splide__arrow--prev {
		border: 1px solid rgba(255, 255, 255, 0.2) !important;
		transform: translate(0, 50%) !important;
	}
	.splide__arrow--next {
		border: 1px solid rgba(255, 255, 255, 0.2) !important;
		transform: translate(0, -50%) !important;
	}
</style>
